import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AccountContext = createContext();

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) throw new Error('useAccount must be used within AccountProvider');
  return context;
};

export const AccountProvider = ({ children }) => {
  const [account, setAccount] = useState(() => {
    try {
      const saved = localStorage.getItem('uas_admin_account');
      return saved ? JSON.parse(saved) : undefined;
    } catch {
      return undefined;
    }
  });

  // On mount: if we have a token, silently re-validate and refresh account data.
  // This picks up any level/permission changes without requiring a sign-out.
  useEffect(() => {
    const stored = (() => {
      try { return JSON.parse(localStorage.getItem('uas_admin_account')); } catch { return null; }
    })();
    if (!stored?.token) return;

    axios.post(
      `${process.env.REACT_APP_API_URL}auth/refresh`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          'api': process.env.REACT_APP_API_KEY,
          'auth': stored.token,
        },
      }
    ).then(res => {
      if (res.data?.success && res.data?.data?.account) {
        setAccount(res.data.data.account);
      } else {
        // Token invalid/expired - sign out
        setAccount(null);
        localStorage.removeItem('uas_admin_account');
      }
    }).catch(() => {
      // Network error - keep existing cached data, don't sign out
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (account) {
      localStorage.setItem('uas_admin_account', JSON.stringify(account));
    } else {
      localStorage.removeItem('uas_admin_account');
    }
  }, [account]);

  const updateAccount = (data) => setAccount(prev => ({ ...prev, ...data }));
  const clearAccount = () => {
    setAccount(null);
    localStorage.removeItem('uas_admin_account');
  };

  return (
    <AccountContext.Provider value={{ account, updateAccount, clearAccount }}>
      {children}
    </AccountContext.Provider>
  );
};
